// AdminVisitsToday.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';
import { MapPin, Building2, Home } from 'lucide-react';
import { FaBookOpen } from 'react-icons/fa';
import StudentDetailsModal from './StudentDetailsModal';
import { MapModal } from './MapModal';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

/** =========================================================
 * WEATHER helpers (local, based on your W2U rules)
 * ========================================================= */

const W2U_SD_URL = 'https://weather2umbrella.com/san-diego,32.7153,-117.156';

// CRA-compatible dynamic imports from /src/opsWeather
let opsWeatherCtx = null;
try {
  // eslint-disable-next-line
  opsWeatherCtx = require.context('./opsWeather', false, /\.(png|jpg|jpeg|svg)$/);
} catch {
  opsWeatherCtx = null;
}
function opsWeatherIconSrc(filename) {
  try {
    if (!opsWeatherCtx || !filename) return null;
    return opsWeatherCtx(`./${filename}`);
  } catch {
    return null;
  }
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickNumber(obj, keys) {
  for (const k of keys) {
    const n = toNum(obj?.[k]);
    if (n != null) return n;
  }
  return null;
}

function normalizeIconFilename(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/^"+|"+$/g, '');
  // remove any path
  s = s.split('/').pop();
  // if no extension, assume png
  if (!/\.(png|jpg|jpeg|svg)$/i.test(s)) s = `${s}.png`;
  return s;
}

// Your alert logic (same thresholds)
function getAlertCode(weather) {
  const iconRaw = String(weather?.icon || '').trim();
  const iconSuffix = iconRaw ? iconRaw.replace(/\.(png|jpg|jpeg|svg)$/i, '').substring(1) : '';

  const pcp = pickNumber(weather, ['pcp', 'precip', 'precip_mm', 'rain', 'rain_mm']) ?? 0;
  const tmp = pickNumber(weather, ['tmp', 'temp', 'temperature', 't']) ?? 0;
  const ws = pickNumber(weather, ['ws', 'wind', 'wind_speed', 'wind_mps']) ?? 0;
  const rh = pickNumber(weather, ['rh', 'humidity', 'hum']) ?? 0;
  const uv = pickNumber(weather, ['uv', 'uvi']) ?? 0;

  if (iconSuffix === '11' && pcp > 0.8) return 'a04b';
  if (iconSuffix === '13' && pcp > 0.6) return 'a05';
  if ((iconSuffix === '14' || iconRaw.trim() === '15') && tmp < -1) return 'a05';
  if (pcp > 1.5 && tmp > 1.1) return 'a03';
  if (ws > 10) return 'a02';
  if (tmp > 32 && rh > 39) return 'a06';
  if (tmp > 37 && uv > 6.5) return 'a06';
  if (tmp > 36 && uv > 7.0) return 'a01';
  if (tmp > 33 && uv > 9) return 'a01';
  if (uv > 9.0) return 'a01';
  if (tmp < -1 && pcp > 0.5) return 'a04';
  if (ws > 7 && pcp > 1.2) return 'a05';
  return null;
}

// Light-mode temperature coloring (Celsius ranges)
const temperatureRangesLight = [
  { range: [-Infinity, -42], color: 'B224B2' },
  { range: [-41, -40], color: 'A724B2' },
  { range: [-39, -35], color: '9B24B2' },
  { range: [-34, -30], color: '8F24B2' },
  { range: [-29, -28], color: '7E24B2' },
  { range: [-27, -26], color: '6D24B2' },
  { range: [-25, -24], color: '5D24B2' },
  { range: [-23, -22], color: '4C24B2' },
  { range: [-21, -20], color: '432DB2' },
  { range: [-19, -18], color: '332DB2' },
  { range: [-17, -16], color: '2D36B2' },
  { range: [-15, -14], color: '2D45B2' },
  { range: [-13, -12], color: '305BBF' },
  { range: [-11, -10], color: '2666BF' },
  { range: [-9, -8], color: '2678BF' },
  { range: [-7, -6], color: '268ABF' },
  { range: [-5, -4], color: '269CBF' },
  { range: [-3, -2], color: '24A2B2' },
  { range: [-1, 0], color: '21A6A6' },
  { range: [1, 2], color: '21A68B' },
  { range: [3, 4], color: '21A671' },
  { range: [5, 6], color: '21A656' },
  { range: [7, 8], color: '21A63C' },
  { range: [9, 10], color: '21A621' },
  { range: [11, 12], color: '3CA621' },
  { range: [13, 14], color: '4CA611' },
  { range: [15, 16], color: '6AA611' },
  { range: [17, 18], color: '89AB00' },
  { range: [19, 20], color: 'B8B800' },
  { range: [21, 22], color: 'CCA300' },
  { range: [23, 24], color: 'CC7E0A' },
  { range: [25, 26], color: 'CC5E14' },
  { range: [27, 28], color: 'CC3914' },
  { range: [29, 30], color: 'BF1D1D' },
  { range: [31, 32], color: 'C4142B' },
  { range: [33, 34], color: 'C71444' },
  { range: [35, 36], color: 'C9145D' },
  { range: [37, 38], color: 'CC1476' },
  { range: [39, 40], color: 'CC148F' },
  { range: [41, 45], color: 'CC149E' },
  { range: [46, Infinity], color: 'CC14AD' },
];

function getTemperatureColorCelsius(tempC) {
  const t = toNum(tempC);
  if (t == null) return '#111827';
  for (const { range, color } of temperatureRangesLight) {
    if (t >= range[0] && t <= range[1]) return `#${color}`;
  }
  return '#111827';
}

function cToF(tempC) {
  const t = toNum(tempC);
  if (t == null) return null;
  return (t * 9) / 5 + 32;
}

function formatF(tempC) {
  const f = cToF(tempC);
  if (f == null) return '—';
  return `${Math.round(f)}°F`;
}

function parseHourLabel(h) {
  if (!h) return null;
  const s = String(h);

  // "2025-12-24 17:00:00" or ISO "2025-12-24T17:00:00Z"
  const m1 = s.match(/T(\d{2}):/);
  if (m1 && m1[1]) return `${m1[1]}h`;

  const m2 = s.match(/\s(\d{2}):/);
  if (m2 && m2[1]) return `${m2[1]}h`;

  const n = Number(s);
  if (Number.isFinite(n)) return `${String(n).padStart(2, '0')}h`;

  return null;
}

/**
 * Parse stamp to minutes since midnight (local, no TZ conversion).
 */
function minutesSinceMidnight(stamp) {
  if (!stamp) return null;
  const s = String(stamp);

  let m = s.match(/T(\d{2}):(\d{2})/);
  if (m && m[1] && m[2]) return Number(m[1]) * 60 + Number(m[2]);

  m = s.match(/\s(\d{2}):(\d{2})/);
  if (m && m[1] && m[2]) return Number(m[1]) * 60 + Number(m[2]);

  m = s.match(/^(\d{2}):(\d{2})/);
  if (m && m[1] && m[2]) return Number(m[1]) * 60 + Number(m[2]);

  const n = Number(s);
  if (Number.isFinite(n)) return n * 60;

  return null;
}

/** =========================================================
 * END WEATHER helpers
 * ========================================================= */

/** ---------- Leaflet marker icon (single pin modals) ---------- */
const leafletPinIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.7/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

/** =========================================================
 * Reference-aligned modals (same as AdminVisits)
 * ========================================================= */

// Single-location modal for G-sign (same as AdminVisits)
function SimpleMapModal({ open, onClose, position, label }) {
  if (!open || !position?.lat || !position?.lon) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg relative p-4">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl"
          onClick={onClose}
        >
          ×
        </button>
        <h3 className="text-sm mb-2">{label}</h3>
        <div className="h-80 w-full">
          <MapContainer
            center={[position.lat, position.lon]}
            zoom={15}
            scrollWheelZoom={false}
            className="h-full w-full rounded"
          >
            <TileLayer attribution="© OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[position.lat, position.lon]} icon={leafletPinIcon}>
              <Popup>{label}</Popup>
            </Marker>
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

// Work location modal (MapModal inside) – same as AdminVisits
function WorkLocationModal({ open, onClose, title, addressLine, dest, teacher }) {
  if (!open) return null;

  const hasCoords =
    (dest?.lat != null && dest?.lon != null) ||
    (dest?.latitude != null && dest?.longitude != null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">{title || 'Work location'}</h3>
            {addressLine ? <p className="text-sm text-gray-600 mt-1">{addressLine}</p> : null}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" title="Close">
            ✕
          </button>
        </div>

        {!hasCoords ? (
          <div className="p-4 text-center text-gray-500 text-sm">Location coordinates are missing.</div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <MapModal open={open} student={dest} teacher={teacher} onClose={onClose} />
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** =========================================================
 * Shared helpers (aligned to AdminVisits)
 * ========================================================= */

function parseDateStr(v) {
  const s = String(v || '');
  const d = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

function parseHHMM(val) {
  const s = String(val || '').trim();
  const m = s.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

function minutesFromHHMM(hhmm) {
  const t = parseHHMM(hhmm);
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function itemTimeHHMM(v) {
  return parseHHMM(v?.scheduled_time) || parseHHMM(v?.from_hhmm) || parseHHMM(v?.scheduled_at) || '';
}

function itemSortValue(v) {
  const d = parseDateStr(v?.scheduled_at);
  const hhmm = itemTimeHHMM(v);
  const mins = minutesFromHHMM(hhmm) ?? 0;

  const y = Number(d.slice(0, 4)) || 0;
  const mo = Number(d.slice(5, 7)) || 0;
  const da = Number(d.slice(8, 10)) || 0;
  const dayKey = y * 372 + mo * 31 + da;

  return dayKey * 1440 + mins;
}

function isWorkItem(v) {
  return v && (v.__kind === 'work' || v._is_work === true);
}

function sortUnified(a, b) {
  const av = itemSortValue(a);
  const bv = itemSortValue(b);
  if (av !== bv) return av - bv;

  const ak = isWorkItem(a) ? 1 : 0;
  const bk = isWorkItem(b) ? 1 : 0;
  if (ak !== bk) return ak - bk;

  const aid = Number(a?.id || a?.work_id || a?.visit_id || 0);
  const bid = Number(b?.id || b?.work_id || b?.visit_id || 0);
  return aid - bid;
}

function groupByDateUnified(items) {
  const acc = {};
  for (const v of items || []) {
    const date = parseDateStr(v?.scheduled_at);
    if (!date) continue;
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
  }
  Object.keys(acc).forEach((d) => acc[d].sort(sortUnified));
  return acc;
}

function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const s = String(hhmm).trim().slice(0, 5);
  const [h, m] = s.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const dt = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  dt.setMinutes(dt.getMinutes() + (mins || 0));
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Badge statusa (same as reference)
function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>
      {children || status}
    </span>
  );
}

/**
 * Disciplinarni bedž (EI / PI / ABA / OT / PT...) – isti princip kao u AdminVisits
 */
function ProgramBadge({ program_type, discipline_code }) {
  const raw = (discipline_code || program_type || '').toString().trim();
  if (!raw) return null;

  const code = raw.replace(/^PROG_/i, '').toUpperCase();

  let extra = '';
  let label = code;

  if (code === 'PI') {
    extra = ' bg-orange-100 text-orange-800 border border-orange-200';
    label = 'PI';
  } else if (code === 'EI') {
    extra = ' bg-blue-100 text-blue-800 border border-blue-200';
    label = 'EI';
  } else if (code === 'ABA') {
    extra = ' bg-purple-100 text-purple-800 border border-purple-200';
    label = 'ABA';
  } else if (code === 'OT') {
    extra = ' bg-teal-100 text-teal-800 border border-teal-200';
    label = 'OT';
  } else if (code === 'PT') {
    extra = ' bg-emerald-100 text-emerald-800 border border-emerald-200';
    label = 'PT';
  } else if (code === 'ST') {
    extra = ' bg-green-100 text-green-800 border border-green-200';
    label = 'ST';
  } else {
    extra = ' border border-gray-400 text-gray-700 bg-gray-50';
    label = code;
  }

  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${extra}`}>{label}</span>;
}

function EventBadge({ isEvent }) {
  if (!isEvent) return null;
  return (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
      EVENT
    </span>
  );
}

// helper za event flag
const isEventVisit = (v) =>
  v.type === 'event' || v.visit_type === 'event' || v.is_event === 1 || v.is_event === true;

function TelehealthBadge({ delivery_mode }) {
  if (delivery_mode === 'telehealth') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
        <span>📹</span>
        <span>Telehealth</span>
      </span>
    );
  }
  if (delivery_mode === 'hybrid') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200">
        <span>📹</span>
        <span>Hybrid</span>
      </span>
    );
  }
  return null;
}

/**
 * Vreme FROM–TO – aligned to AdminVisits (supports scheduled_time for work)
 */
function VisitTimeType({ scheduled_at, scheduled_time, event_end_time }) {
  const from = scheduled_time || scheduled_at?.slice(11, 16) || '';
  let to = null;

  if (event_end_time) {
    to = String(event_end_time).slice(0, 5);
  } else if (from) {
    to = addMinutesHHMM(from, 45);
  }

  return (
    <div className="flex flex-col items-end text-right sm:items-start sm:text-left">
      <span className="font-mono text-base">{from && to ? `${from}–${to}` : from || '-'}</span>
    </div>
  );
}

/** Supervised visit bedž – tirkizni */
function SupervisedBadge() {
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-teal-100 text-teal-800 border border-teal-200">
      Supervised visit
    </span>
  );
}

function buildClinicDestFromLocationRow(loc) {
  if (!loc) return null;

  const parts = [loc.street, loc.city, loc.state, loc.zip, loc.country]
    .map((x) => String(x || '').trim())
    .filter(Boolean);

  const addressLine = parts.join(', ');

  const lat = loc.latitude != null ? Number(loc.latitude) : null;
  const lon = loc.longitude != null ? Number(loc.longitude) : null;

  return {
    id: loc.id,
    name: (loc.name || 'Clinic').trim(),
    address: addressLine || (loc.geocoded_label ? String(loc.geocoded_label) : ''),
    geocoded_label: loc.geocoded_label || null,
    lat,
    lon,
    latitude: lat,
    longitude: lon,
  };
}

function getClinicNameForVisit(v, orgLocationsMap) {
  const mode = String(v?.location_mode || '').toLowerCase();
  if (mode !== 'at_clinic') return '';
  const id = Number(v?.org_location_id);
  if (!id) return '';
  const loc = orgLocationsMap?.[id];
  return (loc?.name || '').trim();
}

function getOrgLocNameForWork(w, orgLocationsMap) {
  if (!w) return '';
  if (String(w.location_mode || '') !== 'org_location') return '';
  const id = Number(w.org_location_id);
  if (!id) return '';
  const loc = orgLocationsMap?.[id];
  return (loc?.name || '').trim();
}

function normalizeTeacherWorkToUnified(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((tw) => {
    const from = tw.from_hhmm || parseHHMM(tw.scheduled_at) || '';
    const to = tw.to_hhmm || tw.end_hhmm || '';

    return {
      ...tw,
      __kind: 'work',
      _is_work: true,
      scheduled_time: from,
      event_end_time: to ? `${String(to).slice(0, 5)}:00` : null,
      work_id: tw.id,
      canceled_reason: tw.canceled_reason || tw.cancel_reason || tw.cancelled_reason || null,
    };
  });
}

function AdminVisitsToday() {
  const [visits, setVisits] = useState([]);
  const [teacherWork, setTeacherWork] = useState([]);

  const [teachers, setTeachers] = useState([]);
  const [orgLocationsMap, setOrgLocationsMap] = useState({});

  const [serverDateStr, setServerDateStr] = useState(null);
  const [serverTimeStr, setServerTimeStr] = useState(null); // "YYYY-MM-DDTHH:mm:ss"

  const [studentDetailsModal, setStudentDetailsModal] = useState({ open: false, student: null });

  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState(null);

  // ✅ MapModal (dual pins) state like AdminVisits
  const [mapModal, setMapModal] = useState({ open: false, student: null, teacher: null });

  const [gsignLog, setGsignLog] = useState({});
  const [gsignMapModal, setGsignMapModal] = useState({ open: false, position: null, date: null });

  // ✅ Work location modal + Work notes modal like AdminVisits
  const [workLocModal, setWorkLocModal] = useState({ open: false, title: '', addressLine: '', dest: null, teacher: null });
  const [workNotesModal, setWorkNotesModal] = useState({
    open: false,
    teacherName: '',
    workType: '',
    workTitle: '',
    notes: '',
    scheduledAt: '',
  });

  // Weather (OPS / W2U)
  const [opsWeather, setOpsWeather] = useState(null);
  const [opsWeatherLoading, setOpsWeatherLoading] = useState(false);

  function round2(num) {
    return Math.round(Number(num) * 100) / 100;
  }

  const teachersMap = useMemo(() => {
    const m = {};
    (teachers || []).forEach((t) => {
      if (t?.id) m[Number(t.id)] = t;
    });
    return m;
  }, [teachers]);

  const resolveTeacherName = (maybeName, teacherId) => {
    const n = String(maybeName || '').trim();
    if (n) return n;
    const tid = Number(teacherId);
    if (tid && teachersMap[tid]?.name) return String(teachersMap[tid].name).trim();
    return tid ? `#${tid}` : '';
  };

  /** -------------------- Weather normalization -------------------- */
  const normalizeOpsWeather = (payload, dateStr, nowIso) => {
    const root = payload?.data || payload || {};
    const cityData = root?.cityData || root?.data?.cityData || null;

    const mapToArray = (obj, dtField = 'dt') => {
      if (!obj || typeof obj !== 'object') return [];
      return Object.entries(obj).map(([k, v]) => ({ ...(v || {}), [dtField]: k }));
    };

    let hourlyList = [];
    if (cityData?.hourly) hourlyList = Array.isArray(cityData.hourly) ? cityData.hourly : mapToArray(cityData.hourly, 'dt');
    else if (root?.hourly) hourlyList = Array.isArray(root.hourly) ? root.hourly : mapToArray(root.hourly, 'dt');
    else if (root?.data?.hourly) hourlyList = Array.isArray(root.data.hourly) ? root.data.hourly : mapToArray(root.data.hourly, 'dt');

    let dailyList = [];
    if (cityData?.daily) dailyList = Array.isArray(cityData.daily) ? cityData.daily : mapToArray(cityData.daily, 'date');
    else if (root?.daily) dailyList = Array.isArray(root.daily) ? root.daily : mapToArray(root.daily, 'date');
    else if (root?.data?.daily) dailyList = Array.isArray(root.data.daily) ? root.data.daily : mapToArray(root.data.daily, 'date');

    const getStamp = (x) => String(x?.dt || x?.date || x?.time || x?.timestamp || x?.valid || '');

    let d = dailyList.find((x) => getStamp(x).slice(0, 10) === dateStr) || dailyList[0] || null;

    let todaysHourly = (hourlyList || []).filter((h) => getStamp(h).slice(0, 10) === dateStr);

    todaysHourly = todaysHourly
      .map((h) => ({ h, stamp: getStamp(h), mins: minutesSinceMidnight(getStamp(h)) }))
      .sort((a, b) => (a.mins ?? 1e9) - (b.mins ?? 1e9))
      .map((x) => x.h);

    if (!d) {
      if (!todaysHourly.length) return null;

      const temps = todaysHourly
        .map((h) => pickNumber(h, ['tmp', 'temp', 'temperature', 't', 't2m']))
        .filter((n) => n != null);

      const tminC = temps.length ? Math.min(...temps) : null;
      const tmaxC = temps.length ? Math.max(...temps) : null;

      const iconRaw = (todaysHourly.find((h) => h?.icon)?.icon || '').toString().trim() || null;

      d = { date: dateStr, icon: iconRaw, tmin: tminC, tmax: tmaxC };
    }

    const iconDaily = normalizeIconFilename(d.icon || d.ico || d.weather_icon);

    const tmaxC =
      pickNumber(d, ['tmax', 'tmax_c', 'max', 'tmp_max', 'temp_max', 'hi', 'tHigh']) ??
      pickNumber(d, ['tmp', 'temp', 'temperature', 't']) ??
      null;

    const tminC =
      pickNumber(d, ['tmin', 'tmin_c', 'min', 'tmp_min', 'temp_min', 'lo', 'tLow']) ??
      pickNumber(d, ['tmp', 'temp', 'temperature', 't']) ??
      null;

    let alertCode = null;
    let alertHour = null;

    const nowMins = minutesSinceMidnight(nowIso);
    const minAlertMins = nowMins != null ? (Math.floor(nowMins / 60) + 1) * 60 : null;

    for (const h of todaysHourly) {
      const stamp = getStamp(h);
      const hm = minutesSinceMidnight(stamp);

      if (minAlertMins != null) {
        if (hm == null) continue;
        if (hm < minAlertMins) continue;
      }

      const code = getAlertCode(h);
      if (code) {
        alertCode = code;
        alertHour = parseHourLabel(stamp);
        break;
      }
    }

    const alertIcon = alertCode ? normalizeIconFilename(`${alertCode}`) : null;

    return { date: dateStr, iconDaily, tmaxC, tminC, alertCode, alertHour, alertIcon };
  };

  const fetchOpsWeather = async () => {
    if (!serverDateStr) return;
    if (!navigator.onLine) {
      setOpsWeather(null);
      return;
    }

    setOpsWeatherLoading(true);
    try {
      let nowIso = serverTimeStr;
      try {
        const tRes = await API.get('/server-time');
        const st = tRes?.data?.serverTime || null;
        if (st) {
          nowIso = st;
          setServerTimeStr(st);
          const d = st.slice(0, 10);
          if (d && d !== serverDateStr) setServerDateStr(d);
        }
      } catch {}

      const res = await API.get('/ops-weather/san-diego');
      if (res?.data?.empty) {
        setOpsWeather(null);
        return;
      }

      const dateStr = (nowIso || serverDateStr || '').slice(0, 10) || serverDateStr;
      const normalized = normalizeOpsWeather(res.data, dateStr, nowIso);
      setOpsWeather(normalized || null);
    } catch {
      setOpsWeather(null);
    } finally {
      setOpsWeatherLoading(false);
    }
  };

  /** -------------------- Init: server time, teachers, org locations -------------------- */
  useEffect(() => {
    (async () => {
      try {
        const stRes = await API.get('/server-time');
        const st = stRes?.data?.serverTime || null;
        if (st) {
          setServerTimeStr(st);
          setServerDateStr(st.slice(0, 10));
        }
      } catch {}

      // teachers list (needed for teacher_work/range)
      try {
        const tRes = await API.get('/teachers');
        setTeachers(tRes.data || []);
      } catch {
        setTeachers([]);
      }

      // org locations map (needed for at_clinic + work org_location label)
      try {
        const res = await API.get('/org-locations');
        const rows = res.data?.locations || [];
        const m = {};
        rows.forEach((r) => {
          if (r?.id) m[Number(r.id)] = r;
        });
        setOrgLocationsMap(m);
      } catch {
        setOrgLocationsMap({});
      }
    })();
  }, []);

  /** -------------------- Fetch today visits + work -------------------- */
  const fetchTodayVisits = async (dateStr) => {
    const params = { date_from: dateStr, date_to: dateStr };
    const res = await API.get('/admin/visits', { params });
    const rows = res.data || [];
    setVisits(rows);

    if (rows.length) {
      const ids = rows.map((v) => v.visit_id).filter(Boolean).join(',');
      if (ids) {
        const logs = await API.get('/visit-gsign-log', { params: { visit_ids: ids } });
        setGsignLog(logs.data || {});
      } else {
        setGsignLog({});
      }
    } else {
      setGsignLog({});
    }
  };

  const fetchTodayWork = async (dateStr) => {
    const teacherIds = (teachers || []).map((t) => String(t.id)).filter(Boolean);
    if (!teacherIds.length) {
      setTeacherWork([]);
      return;
    }
    try {
      const wRes = await API.get('/teacher-work/range', {
        params: {
          date_from: dateStr,
          date_to: dateStr,
          teacher_ids: teacherIds.join(','),
        },
      });
      const w = normalizeTeacherWorkToUnified(wRes.data || []);
      setTeacherWork(w);
    } catch {
      setTeacherWork([]);
    }
  };

  useEffect(() => {
    if (!serverDateStr) return;
    fetchTodayVisits(serverDateStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverDateStr]);

  useEffect(() => {
    if (!serverDateStr) return;
    if (!teachers.length) return;
    fetchTodayWork(serverDateStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverDateStr, teachers.length]);

  /** -------------------- Weather refresh loop -------------------- */
  useEffect(() => {
    if (!serverDateStr) return;

    fetchOpsWeather();

    const t = setInterval(() => {
      if (navigator.onLine) fetchOpsWeather();
    }, 10 * 60 * 1000);

    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [serverDateStr]);

  const WeatherWidget = () => {
    if (!navigator.onLine) return null;

    if (opsWeatherLoading) {
      return (
        <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border bg-white text-xs text-slate-500">
          <span className="inline-block w-2 h-2 rounded-full bg-slate-300" />
          Weather…
        </div>
      );
    }
    if (!opsWeather) return null;

    const dailySrc = opsWeatherIconSrc(opsWeather.iconDaily);
    const alertSrc = opsWeather.alertIcon ? opsWeatherIconSrc(opsWeather.alertIcon) : null;

    const tmaxColor = getTemperatureColorCelsius(opsWeather.tmaxC);
    const tminColor = getTemperatureColorCelsius(opsWeather.tminC);

    return (
      <button
        type="button"
        onClick={() => window.open(W2U_SD_URL, '_blank', 'noopener,noreferrer')}
        className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border bg-white hover:bg-slate-50 transition-colors"
        title="Open Weather2Umbrella (San Diego)"
      >
        {dailySrc ? (
          <img src={dailySrc} alt="weather" className="w-6 h-6 object-contain" />
        ) : (
          <span className="text-xs text-slate-500">Weather</span>
        )}

        <div className="flex items-center gap-1 text-xs font-semibold">
          <span style={{ color: tminColor }}>{formatF(opsWeather.tminC)}</span>
          <span className="text-slate-400">/</span>
          <span style={{ color: tmaxColor }}>{formatF(opsWeather.tmaxC)}</span>
        </div>

        {alertSrc ? (
          <div className="flex items-center gap-1 ml-1">
            <img src={alertSrc} alt="alert" className="w-5 h-5 object-contain" />
            {opsWeather.alertHour ? (
              <span className="text-[11px] font-semibold text-slate-700">{opsWeather.alertHour}</span>
            ) : null}
          </div>
        ) : null}
      </button>
    );
  };

  /** -------------------- Student + notes history -------------------- */
  const openStudentModal = async (studentId) => {
    try {
      const res = await API.get(`/students/${studentId}/full`);
      setStudentDetailsModal({ open: true, student: res.data });
    } catch {
      alert('Failed to load student details');
    }
  };

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  /** -------------------- Map openers (aligned to AdminVisits) -------------------- */

  // Visit: use /visit-map-locations and override student dest if at_clinic
  const openMapModalVisit = async (visit) => {
    try {
      const res = await API.get('/visit-map-locations', {
        params: { student_id: visit.student_id, teacher_id: visit.teacher_id },
      });

      let studentObj = res.data.student || null;
      const teacherObj = res.data.teacher || null;

      const mode = String(visit.location_mode || 'at_home').toLowerCase();
      const isClinic = mode === 'at_clinic';

      if (isClinic && visit.org_location_id) {
        const loc = orgLocationsMap?.[Number(visit.org_location_id)];
        const clinicDest = buildClinicDestFromLocationRow(loc);
        if (clinicDest?.lat != null && clinicDest?.lon != null) {
          studentObj = clinicDest;
        }
      }

      setMapModal({ open: true, student: studentObj, teacher: teacherObj });
    } catch {
      alert('Failed to fetch map data');
    }
  };

  const openWorkNotesModal = (w) => {
    const teacherName = resolveTeacherName(w.teacher_name || w.teacher_full_name || w.teacher, w.teacher_id);
    const workType = String(w.work_type || 'other');
    setWorkNotesModal({
      open: true,
      teacherName,
      workType,
      workTitle: String(w.title || '').trim(),
      notes: String(w.notes || '').trim(),
      scheduledAt: String(w.scheduled_at || ''),
    });
  };

  const openWorkLocationModal = (w) => {
    const workType = String(w.work_type || 'other');

    let locationName = '';
    let addressLine = '';
    let lat = null;
    let lon = null;

    if (w.location_mode === 'org_location') {
      const fallbackLoc = w.org_location_id ? orgLocationsMap?.[Number(w.org_location_id)] : null;

      locationName =
        String(w.org_location_name || '').trim() ||
        String(fallbackLoc?.name || '').trim();

      const parts = [
        w.org_location_street ?? fallbackLoc?.street,
        w.org_location_city ?? fallbackLoc?.city,
        w.org_location_state ?? fallbackLoc?.state,
        w.org_location_zip ?? fallbackLoc?.zip,
        w.org_location_country ?? fallbackLoc?.country,
      ]
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      addressLine = parts.join(', ');

      const latSrc = w.org_location_latitude ?? fallbackLoc?.latitude;
      const lonSrc = w.org_location_longitude ?? fallbackLoc?.longitude;

      lat = latSrc != null ? Number(latSrc) : null;
      lon = lonSrc != null ? Number(lonSrc) : null;
    } else if (w.location_mode === 'teacher_home') {
      locationName = 'Teacher home';
      const parts = [w.teacher_street, w.teacher_city, w.teacher_state, w.teacher_zip, w.teacher_country]
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      addressLine = parts.join(', ');
      lat = w.teacher_lat != null ? Number(w.teacher_lat) : null;
      lon = w.teacher_lon != null ? Number(w.teacher_lon) : null;
    } else if (w.location_mode === 'remote') {
      locationName = 'Remote';
      addressLine = 'Remote work';
    }

    const title = locationName ? `Work: ${workType} - ${locationName}` : `Work: ${workType}`;

    const dest = {
      name: locationName || 'Work',
      address: addressLine,
      lat,
      lon,
      latitude: lat,
      longitude: lon,
    };

    setWorkLocModal({
      open: true,
      title,
      addressLine,
      dest,
      teacher: null,
    });
  };

  /** -------------------- Unified rendering -------------------- */
  const unifiedItems = useMemo(() => {
    const merged = [...(visits || []), ...(teacherWork || [])];
    merged.sort(sortUnified);
    return merged;
  }, [visits, teacherWork]);

  const grouped = useMemo(() => groupByDateUnified(unifiedItems), [unifiedItems]);

  return (
    <div className="w-full px-1 sm:px-1">
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-lg font-bold">Today&apos;s Schedule</h2>
        <WeatherWidget />
      </div>

      {navigator.onLine && opsWeather && (
        <button
          type="button"
          onClick={() => window.open(W2U_SD_URL, '_blank', 'noopener,noreferrer')}
          className="sm:hidden mb-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50"
          title="Open Weather2Umbrella (San Diego)"
        >
          <div className="flex items-center gap-2">
            {opsWeatherIconSrc(opsWeather.iconDaily) ? (
              <img
                src={opsWeatherIconSrc(opsWeather.iconDaily)}
                alt="weather"
                className="w-7 h-7 object-contain"
              />
            ) : null}
            <div className="flex flex-col items-start">
              <div className="text-xs font-semibold text-slate-800">San Diego</div>
              <div className="text-[11px] text-slate-500">Tap to open forecast</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold">
              <span style={{ color: getTemperatureColorCelsius(opsWeather.tminC) }}>
                {formatF(opsWeather.tminC)}
              </span>
              <span className="text-slate-400 mx-1">/</span>
              <span style={{ color: getTemperatureColorCelsius(opsWeather.tmaxC) }}>
                {formatF(opsWeather.tmaxC)}
              </span>
            </div>

            {opsWeather.alertIcon && opsWeatherIconSrc(opsWeather.alertIcon) ? (
              <div className="flex items-center gap-1">
                <img
                  src={opsWeatherIconSrc(opsWeather.alertIcon)}
                  alt="alert"
                  className="w-6 h-6 object-contain"
                />
                {opsWeather.alertHour ? (
                  <span className="text-[11px] font-semibold text-slate-700">
                    {opsWeather.alertHour}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </button>
      )}

      {!serverDateStr ? (
        <div className="p-4 text-gray-500 text-center">Loading server time...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-gray-500 mt-6">No visits or work blocks found for today.</div>
      ) : (
        Object.entries(grouped).map(([date, group]) => (
          <div key={date} className="mb-6 shadow rounded-lg bg-white border">
            <div className="font-bold text-base px-4 py-3 bg-gray-50 rounded-t-lg flex items-center">
              <span>{date}</span>
              <span className="ml-3 bg-blue-200 text-blue-900 text-xs px-2 py-0.5 rounded-full">
                {group.length} items
              </span>
            </div>

            {/* DESKTOP */}
            <div className="overflow-x-auto hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1">Time</th>
                    <th className="px-2 py-1">Student / Work</th>
                    <th className="px-2 py-1">Teacher</th>
                    <th className="px-2 py-1 w-40">Status</th>
                    <th className="px-2 py-1">Reassigned</th>
                    <th className="px-2 py-1">G-sign</th>
                  </tr>
                </thead>
                <tbody>
                  {group.map((v) => {
                    const work = isWorkItem(v);

                    // WORK ROW (aligned to AdminVisits)
                    if (work) {
                      const from = v.scheduled_time || parseHHMM(v.from_hhmm) || parseHHMM(v.scheduled_at);
                      const to =
                        v.to_hhmm ||
                        parseHHMM(v.end_time) ||
                        (v.event_end_time ? String(v.event_end_time).slice(0, 5) : '');
                      const workType = String(v.work_type || 'other');

                      const teacherName = resolveTeacherName(
                        v.teacher_name || v.teacher_full_name || v.teacher,
                        v.teacher_id
                      );

                      const titleText = `Work: ${workType}`;
                      const orgLocName = getOrgLocNameForWork(v, orgLocationsMap);
                      const workCanceledReason = String(v.canceled_reason || '').trim();

                      return (
                        <tr key={`work_${v.work_id || v.id}`} className="hover:bg-gray-50">
                          <td className="px-2 py-1">
                            <VisitTimeType
                              scheduled_at={v.scheduled_at}
                              scheduled_time={from}
                              event_end_time={to ? `${String(to).slice(0, 5)}:00` : null}
                            />
                          </td>

                          <td className="px-2 py-1">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Building2 className="w-4 h-4 text-slate-600" />
                                <span className="font-semibold text-sky-900">{titleText}</span>

                                <button
                                  onClick={() => openWorkNotesModal(v)}
                                  className="text-purple-700 hover:text-purple-900"
                                  title="Show work notes"
                                >
                                  <FaBookOpen size={18} />
                                </button>

                                <button
                                  onClick={() => openWorkLocationModal(v)}
                                  className="text-gray-400 hover:text-blue-600"
                                  title="Show work location on map"
                                >
                                  <MapPin size={18} />
                                </button>
                              </div>

                              {v.location_mode === 'org_location' ? (
                                <div className="mt-0.5 text-[11px] text-slate-500">
                                  {orgLocName ? (
                                    <>
                                      <span className="font-semibold">Location:</span> {orgLocName}
                                    </>
                                  ) : (
                                    <span className="italic">Location</span>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          </td>

                          <td className="px-2 py-1">
                            <div>{teacherName}</div>
                          </td>

                          <td className="px-2 py-1 capitalize">
                            <StatusBadge status={v.status}>{v.status}</StatusBadge>
                            {v.status === 'canceled' && workCanceledReason ? (
                              <div className="text-xs text-purple-700 mt-1 italic">{workCanceledReason}</div>
                            ) : null}
                          </td>

                          <td className="px-2 py-1 text-xs text-gray-400">N/A</td>
                          <td className="px-2 py-1 text-center text-xs text-gray-400">N/A</td>
                        </tr>
                      );
                    }

                    // VISIT ROW
                    const eventFlag = isEventVisit(v);
                    const isSupervised = !!v.trainee_teacher_id;

                    const locMode = String(v.location_mode || 'at_home').toLowerCase();
                    const locIcon =
                      locMode === 'at_clinic' ? (
                        <Building2 className="w-4 h-4 text-slate-600" />
                      ) : (
                        <Home className="w-4 h-4 text-slate-600" />
                      );
                    const clinicName = getClinicNameForVisit(v, orgLocationsMap);

                    return (
                      <tr key={v.visit_id} className="hover:bg-gray-50">
                        <td className="px-2 py-1">
                          <VisitTimeType scheduled_at={v.scheduled_at} event_end_time={v.event_end_time} />
                        </td>

                        <td className="px-2 py-1">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1 flex-wrap">
                              {locIcon}
                              <span
                                onClick={() => openStudentModal(v.student_id)}
                                className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                                title="Student Details"
                              >
                                {v.student_name}
                              </span>

                              {eventFlag ? (
                                <EventBadge isEvent={true} />
                              ) : (
                                <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                              )}

                              <button
                                type="button"
                                title="Show on Map"
                                className="text-gray-400 hover:text-blue-600 p-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMapModalVisit(v);
                                }}
                              >
                                <MapPin size={16} />
                              </button>

                              <button
                                type="button"
                                title="Show Notes History"
                                className="text-purple-700 hover:text-purple-900 p-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openNotesHistoryModal(v.student_id, v.student_name);
                                }}
                              >
                                <FaBookOpen size={16} />
                              </button>
                            </div>

                            {locMode === 'at_clinic' ? (
                              <div className="mt-0.5 text-[11px] text-slate-500">
                                {clinicName ? (
                                  <>
                                    <span className="font-semibold">Clinic:</span> {clinicName}
                                  </>
                                ) : (
                                  <span className="italic">Clinic</span>
                                )}
                              </div>
                            ) : null}

                            <div>
                              <TelehealthBadge delivery_mode={v.delivery_mode} />
                            </div>
                          </div>
                        </td>

                        <td className="px-2 py-1">
                          <div>{v.teacher_name}</div>
                          {v.teacher_id !== v.original_teacher_id && v.original_teacher_name && (
                            <div className="text-xs text-gray-400 mt-1">Default: {v.original_teacher_name}</div>
                          )}
                          {v.trainee_teacher_name && (
                            <div className="text-xs text-teal-700 mt-0.5">Trainee: {v.trainee_teacher_name}</div>
                          )}
                        </td>

                        <td className="px-2 py-1 capitalize">
                          <StatusBadge status={v.status}>{v.status}</StatusBadge>
                          {isSupervised && (
                            <div className="mt-1">
                              <SupervisedBadge />
                            </div>
                          )}
                          {v.status === 'canceled' && v.notes && (
                            <div className="text-xs text-purple-700 mt-1 italic">{v.notes}</div>
                          )}
                        </td>

                        <td className="px-2 py-1 text-xs text-gray-400">
                          {v.is_reassigned ? (
                            <StatusBadge status={v.reassigned_status}>{v.reassigned_status}</StatusBadge>
                          ) : (
                            'Default Teacher'
                          )}
                        </td>

                        <td className="px-2 py-1 text-center">
                          {v.g_sign_confirmed ? (() => {
                            const gsign = gsignLog[v.visit_id];
                            const hasGsign = gsign && gsign.lat != null && gsign.lon != null;
                            const gLat2 = hasGsign ? round2(gsign.lat) : null;
                            const gLon2 = hasGsign ? round2(gsign.lon) : null;
                            const checkinLat2 = v.checkin_lat != null ? round2(v.checkin_lat) : null;
                            const checkinLon2 = v.checkin_lon != null ? round2(v.checkin_lon) : null;
                            const checkoutLat2 = v.checkout_lat != null ? round2(v.checkout_lat) : null;
                            const checkoutLon2 = v.checkout_lon != null ? round2(v.checkout_lon) : null;

                            let color = 'text-gray-400';
                            if (hasGsign) {
                              const checkinMatch =
                                checkinLat2 != null &&
                                checkinLon2 != null &&
                                gLat2 === checkinLat2 &&
                                gLon2 === checkinLon2;
                              const checkoutMatch =
                                checkoutLat2 != null &&
                                checkoutLon2 != null &&
                                gLat2 === checkoutLat2 &&
                                gLon2 === checkoutLon2;
                              color = checkinMatch || checkoutMatch ? 'text-green-700' : 'text-red-600';
                            }

                            return (
                              <div className="flex flex-col items-center">
                                <div className="flex flex-row items-center gap-1">
                                  <span className="text-green-600 text-md">✅</span>
                                  {hasGsign && (
                                    <button
                                      title="Show confirmation location"
                                      className={color}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setGsignMapModal({
                                          open: true,
                                          position: { lat: Number(gsign.lat), lon: Number(gsign.lon) },
                                          date: gsign.confirmed_at,
                                        });
                                      }}
                                    >
                                      <MapPin size={16} />
                                    </button>
                                  )}
                                </div>
                                {gsign && (
                                  <span className={`text-xs mt-0.5 ${color}`}>
                                    {gsign.confirmed_at?.slice(0, 16).replace('T', ' ')}
                                  </span>
                                )}
                              </div>
                            );
                          })() : (
                            <span className="text-red-600 text-md">❌</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE */}
            <div className="sm:hidden flex flex-col gap-2 p-2">
              {group.map((v) => {
                const work = isWorkItem(v);

                if (work) {
                  const from = v.scheduled_time || parseHHMM(v.from_hhmm) || parseHHMM(v.scheduled_at);
                  const to =
                    v.to_hhmm ||
                    parseHHMM(v.end_time) ||
                    (v.event_end_time ? String(v.event_end_time).slice(0, 5) : '');
                  const workType = String(v.work_type || 'other');

                  const teacherName = resolveTeacherName(
                    v.teacher_name || v.teacher_full_name || v.teacher,
                    v.teacher_id
                  );

                  const titleText = `Work: ${workType}`;
                  const orgLocName = getOrgLocNameForWork(v, orgLocationsMap);
                  const workCanceledReason = String(v.canceled_reason || '').trim();

                  return (
                    <div key={`work_${v.work_id || v.id}`} className="rounded-2xl border shadow p-3 mb-1 bg-white">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 flex items-center gap-2 flex-wrap">
                          <Building2 className="w-4 h-4 text-slate-600" />
                          <span className="font-semibold text-sky-900">{titleText}</span>

                          <button
                            onClick={() => openWorkNotesModal(v)}
                            className="text-purple-700 hover:text-purple-900"
                            title="Show work notes"
                          >
                            <FaBookOpen size={16} />
                          </button>

                          <button
                            onClick={() => openWorkLocationModal(v)}
                            className="text-gray-400 hover:text-blue-600"
                            title="Show on Map"
                          >
                            <MapPin size={16} />
                          </button>
                        </div>

                        <div className="flex-1 text-right">
                          <VisitTimeType
                            scheduled_at={v.scheduled_at}
                            scheduled_time={from}
                            event_end_time={to ? `${String(to).slice(0, 5)}:00` : null}
                          />
                        </div>
                      </div>

                      {v.location_mode === 'org_location' ? (
                        <div className="mt-1 text-[11px] text-slate-500">
                          {orgLocName ? (
                            <>
                              <span className="font-semibold">Location:</span> {orgLocName}
                            </>
                          ) : (
                            <span className="italic">Location</span>
                          )}
                        </div>
                      ) : null}

                      <div className="flex flex-wrap gap-2 items-center mt-2">
                        <StatusBadge status={v.status}>{v.status}</StatusBadge>
                        {v.status === 'canceled' && workCanceledReason ? (
                          <div className="w-full text-xs text-purple-700 italic">{workCanceledReason}</div>
                        ) : null}
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <span className="block text-xs text-gray-600">Teacher</span>
                          <span>{teacherName}</span>
                        </div>
                        <div>
                          <span className="block text-xs text-gray-600">G-sign</span>
                          <span className="text-xs text-gray-400">N/A</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // VISIT CARD
                const eventFlag = isEventVisit(v);
                const isSupervised = !!v.trainee_teacher_id;

                const locMode = String(v.location_mode || 'at_home').toLowerCase();
                const locIcon =
                  locMode === 'at_clinic' ? (
                    <Building2 className="w-4 h-4 text-slate-600" />
                  ) : (
                    <Home className="w-4 h-4 text-slate-600" />
                  );
                const clinicName = getClinicNameForVisit(v, orgLocationsMap);

                const gsign = gsignLog[v.visit_id];
                const hasGsign = gsign && gsign.lat != null && gsign.lon != null;

                return (
                  <div key={v.visit_id} className="rounded-2xl border shadow p-3 mb-1 bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        {locIcon}
                        <span
                          onClick={() => openStudentModal(v.student_id)}
                          className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                        >
                          {v.student_name}
                        </span>

                        {eventFlag ? (
                          <EventBadge isEvent={true} />
                        ) : (
                          <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                        )}

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openMapModalVisit(v);
                          }}
                          className="ml-1 text-gray-400 hover:text-blue-600"
                          title="Show on Map"
                        >
                          <MapPin size={16} />
                        </button>

                        <button
                          onClick={() => openNotesHistoryModal(v.student_id, v.student_name)}
                          className="ml-1 text-purple-700 hover:text-purple-900"
                          title="Show Notes History"
                        >
                          <FaBookOpen size={16} />
                        </button>
                      </div>

                      <div className="flex-1 text-right">
                        <VisitTimeType scheduled_at={v.scheduled_at} event_end_time={v.event_end_time} />
                      </div>
                    </div>

                    {locMode === 'at_clinic' ? (
                      <div className="mt-1 text-[11px] text-slate-500">
                        {clinicName ? (
                          <>
                            <span className="font-semibold">Clinic:</span> {clinicName}
                          </>
                        ) : (
                          <span className="italic">Clinic</span>
                        )}
                      </div>
                    ) : null}

                    <div className="mt-1">
                      <TelehealthBadge delivery_mode={v.delivery_mode} />
                    </div>

                    <div className="flex flex-wrap gap-2 items-center mt-2">
                      <StatusBadge status={v.status}>{v.status}</StatusBadge>
                      {isSupervised && <SupervisedBadge />}
                      {v.status === 'canceled' && v.notes ? (
                        <span className="text-xs text-purple-700 italic">{v.notes}</span>
                      ) : null}
                      {v.teacher_id !== v.original_teacher_id && v.reassigned_status ? (
                        <span className={`text-xs ${v.reassigned_status === 'rejected' ? 'text-red-700' : 'text-orange-700'}`}>
                          Substitute: {v.reassigned_status}
                        </span>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div>
                        <span className="block text-xs text-gray-600">Teacher</span>
                        <span>{v.teacher_name}</span>
                        {v.teacher_id !== v.original_teacher_id && v.original_teacher_name ? (
                          <div className="text-xs text-gray-400 mt-1">Default: {v.original_teacher_name}</div>
                        ) : null}
                        {v.trainee_teacher_name ? (
                          <div className="text-xs text-teal-700 mt-0.5">Trainee: {v.trainee_teacher_name}</div>
                        ) : null}
                      </div>

                      <div>
                        <span className="block text-xs text-gray-600">G-sign</span>
                        {v.g_sign_confirmed ? (
                          <div className="flex items-center gap-1">
                            <span className="text-green-600 text-md">✅</span>
                            {hasGsign ? (
                              <button
                                className="text-slate-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGsignMapModal({
                                    open: true,
                                    position: { lat: Number(gsign.lat), lon: Number(gsign.lon) },
                                    date: gsign.confirmed_at,
                                  });
                                }}
                              >
                                <MapPin size={16} />
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-red-600 text-md">❌</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Modals */}
      <StudentDetailsModal
        open={studentDetailsModal.open}
        onClose={() => setStudentDetailsModal({ open: false, student: null })}
        student={studentDetailsModal.student}
      />

      <MapModal
        open={mapModal.open}
        onClose={() => setMapModal({ open: false, student: null, teacher: null })}
        student={mapModal.student}
        teacher={mapModal.teacher}
      />

      <WorkLocationModal
        open={!!workLocModal.open}
        onClose={() => setWorkLocModal({ open: false, title: '', addressLine: '', dest: null, teacher: null })}
        title={workLocModal.title}
        addressLine={workLocModal.addressLine}
        dest={workLocModal.dest}
        teacher={workLocModal.teacher}
      />

      <SimpleMapModal
        open={gsignMapModal.open}
        onClose={() => setGsignMapModal({ open: false, position: null, date: null })}
        position={gsignMapModal.position}
        label={`Parent confirmed location${
          gsignMapModal.date ? ` at ${gsignMapModal.date.slice(0, 16).replace('T', ' ')}` : ''
        }`}
      />

      {/* Work Notes Modal */}
      {workNotesModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="text-lg font-bold truncate">Work Notes — {workNotesModal.workType}</h3>
                <div className="text-xs text-gray-500 mt-1">
                  {workNotesModal.teacherName ? `Teacher: ${workNotesModal.teacherName}` : null}
                  {workNotesModal.scheduledAt ? ` • ${String(workNotesModal.scheduledAt).slice(0, 16).replace('T', ' ')}` : null}
                </div>
              </div>
              <button className="text-gray-500" onClick={() => setWorkNotesModal((s) => ({ ...s, open: false }))}>
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div className="border rounded p-3 bg-gray-50">
                <div className="text-xs text-gray-500 mb-1">Title</div>
                <div className="font-semibold text-slate-800">
                  {workNotesModal.workTitle || <span className="italic text-gray-400">No title</span>}
                </div>
              </div>

              <div className="border rounded p-3 bg-gray-50">
                <div className="text-xs text-gray-500 mb-1">Notes</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">
                  {workNotesModal.notes || <span className="italic text-gray-400">No notes</span>}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setWorkNotesModal((s) => ({ ...s, open: false }))}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes history modal */}
      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">Notes History: {notesHistoryStudent}</h3>
              <button className="text-gray-500" onClick={() => setShowNotesHistory(false)}>
                ✕
              </button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notes found for this student.</div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-semibold text-blue-900">{entry.teacher_name}</span>
                        {entry.discipline_name && (
                          <span className="text-xs text-gray-500">{entry.discipline_name}</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at ? entry.scheduled_at.slice(0, 16).replace('T', ' ') : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {entry.domains && entry.domains.length > 0 ? (
                        entry.domains.map((dom) =>
                          dom.note && dom.note.trim() !== '' ? (
                            <div key={dom.domain_id}>
                              <span className="font-semibold">{dom.name || dom.code || 'Domain'}:</span>{' '}
                              <span>{dom.note}</span>
                            </div>
                          ) : null
                        )
                      ) : (
                        <span className="text-xs text-gray-500 italic">No domain notes for this visit.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVisitsToday;












